import { useState } from 'react';
import {
  AckBadge,
  OpsEmpty,
  OpsError,
  OpsLoading,
  OpsPagination,
  OpsTable,
  StatusBadge,
  Th,
  formatOpsDate,
} from '@/components/OpsTable';
import { ApiCallError } from '@/lib/api';
import {
  MESSAGE_DIRECTIONS,
  MESSAGE_STATUSES,
  useOpsMessages,
  type MessageDirection,
  type MessageStatus,
} from '@/hooks/useOpsDashboard';

export function Messages() {
  const [status, setStatus] = useState<MessageStatus | 'all'>('all');
  const [direction, setDirection] = useState<MessageDirection | 'all'>('all');
  const [offset, setOffset] = useState(0);

  const { data, isLoading, error } = useOpsMessages({ status, direction, offset });
  const items = data?.items ?? [];
  const pagination = data?.pagination;

  return (
    <div>
      <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Messages</h1>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="inline-flex flex-wrap rounded-md border border-gray-300 bg-white shadow-sm dark:border-gray-600 dark:bg-gray-800">
          {MESSAGE_DIRECTIONS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => {
                setDirection(d);
                setOffset(0);
              }}
              className={`px-3 py-1.5 text-sm first:rounded-l-md last:rounded-r-md ${
                direction === d
                  ? 'bg-indigo-600 text-white'
                  : 'text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700'
              }`}
            >
              {d}
            </button>
          ))}
        </div>

        <select
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as MessageStatus | 'all');
            setOffset(0);
          }}
          className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200"
        >
          {MESSAGE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <OpsError message={error instanceof ApiCallError ? error.message : 'Could not load messages.'} />
      )}

      {isLoading ? (
        <OpsLoading />
      ) : items.length === 0 ? (
        <OpsEmpty description="No messages yet." />
      ) : (
        <>
          <OpsTable>
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <Th>Direction</Th>
                <Th>Status</Th>
                <Th>Sender</Th>
                <Th>Receiver</Th>
                <Th>Identifier</Th>
                <Th>Date</Th>
                <Th>Ack</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white dark:divide-gray-700 dark:bg-card-dark">
              {items.map((item) => (
                <tr key={item.id} className="transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-800">
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 first:pl-4 dark:text-gray-400 sm:first:pl-6">
                    {item.direction}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <StatusBadge status={item.status} />
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {item.senderSor ?? '—'}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {item.receiverSor ?? '—'}
                  </td>
                  <td className="max-w-xs truncate px-3 py-4 text-sm text-gray-900 dark:text-white">
                    {item.communicationIdentifier.replace(/^urn:uuid:/i, '').slice(0, 12)}…
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {formatOpsDate(
                      item.direction === 'in'
                        ? item.receivedAt ?? item.createdAt
                        : item.sentAt ?? item.createdAt,
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-4 text-sm">
                    <AckBadge ack={item.latestAcknowledgement} />
                  </td>
                </tr>
              ))}
            </tbody>
          </OpsTable>
          {pagination && (
            <OpsPagination
              total={pagination.total}
              limit={pagination.limit}
              offset={pagination.offset}
              onOffsetChange={setOffset}
            />
          )}
        </>
      )}
    </div>
  );
}
